/**
 * WindAnalysisLayer.tsx — Triangolo di propagazione dell'incendio
 *
 * LOGICA OPERATIVA:
 *   Il triangolo si costruisce a partire dal PUNTO D'INNESCO + VENTO.
 *   Non richiede il perimetro — è uno strumento PREDITTIVO che il DOS
 *   usa dall'inizio dell'intervento per capire dove andrà il fuoco.
 *
 *   Geometria:
 *     - Apice (origine) = punto d'innesco
 *     - Direzione = propagazione del vento (direzione vento + 180°)
 *     - Apertura laterale = funzione della velocità del vento
 *     - Lunghezza = funzione della velocità del vento
 *
 *   Schema colori AIB (centralizzato in AIB_COLORS):
 *     Asse propagazione → fiancoDestro (arancione)
 *     Cono fill         → conoPropagazione (rosso semitrasparente)
 *     Lato sinistro     → fiancoSinistro (giallo)
 *     Lato destro       → fiancoDestro (arancione)
 *     Asse direttrice   → assePropagazion (azzurro tratteggiato)
 *
 * © 2024 IRTILAC — Angelo Marino — Tutti i diritti riservati.
 */

import React from 'react';
import { Circle, CircleMarker, Polygon, Polyline, Tooltip } from 'react-leaflet';
import { metersToLatLng, normalizeDegrees } from './tacticalMapGeometry';
import { AIB_COLORS } from '@/config/sitac';
import { getWindLabel } from '../../map/layers/WindOverlay';

// ─── Fattori combustibile (velocità relativa a pascolo/erbacee = 1.0)
// Basati su velocità media a vento 30 km/h (m/min) — contesto mediterraneo
export const COMBUSTIBILE_FACTORS: Record<string, number> = {
  pascolo:          1.00,  // 35 m/min — riferimento
  sterpaglia:       0.71,  // 25 m/min
  macchia_bassa:    0.83,  // 29 m/min — oli essenziali
  macchia_alta:     0.50,  // 17.5 m/min — fiamma alta, lenta
  gariga:           1.21,  // 42.5 m/min — ampelodesma, molto comune
  pineta:           1.14,  // 40 m/min — rischio chioma, spotting
  lecceta:          0.29,  // 10 m/min — fuoco di superficie
  eucalipteto:      2.00,  // 70 m/min — ESTREMO, spotting fino a 2 km
  lettiera_conifere: 0.64, // 22.5 m/min — braci durature
};

// ─── Distanza massima di spotting per combustibili a rischio (metri)
const SPOTTING_FUELS: Record<string, number> = {
  eucalipteto:       2000,  // braci fino a 2 km
  pineta:            1200,  // spotting chioma fino a 1.2 km
  lettiera_conifere:  600,  // braci durature fino a 600 m
};

interface WindAnalysisLayerProps {
  directionDeg: number;   // direzione DA cui viene il vento (meteorologica)
  speedKmh: number;       // velocità vento in km/h
  puntoInnesco: [number, number] | null;  // apice del triangolo
  tipoVegetazione?: string; // tipo combustibile — scala la lunghezza del triangolo
}

// ─── Calcola la geometria del triangolo ───────────────────────
function buildTriangle(
  innesco: [number, number],
  propagationDeg: number,
  speedKmh: number,
  tipoVegetazione?: string
) {
  const factor = (tipoVegetazione && COMBUSTIBILE_FACTORS[tipoVegetazione])
    ? COMBUSTIBILE_FACTORS[tipoVegetazione]
    : 1.0;

  const lunghezzaBase =
    speedKmh >= 50 ? 3000 :
    speedKmh >= 35 ? 2200 :
    speedKmh >= 20 ? 1500 :
    speedKmh >= 10 ? 900  :
    500;

  const lunghezza = Math.round(lunghezzaBase * factor);

  // vento forte → cono stretto; vento debole → cono largo
  const semiAngleDeg =
    speedKmh >= 50 ? 13 :
    speedKmh >= 35 ? 17 :
    speedKmh >= 20 ? 23 :
    speedKmh >= 10 ? 30 :
    38;

  // ROS = Rate Of Spread in m/min (modello semplificato mediterraneo)
  // Riferimento: pascolo = 35 m/min a 30 km/h (proporzionale lineare)
  const ros = Math.max(1, Math.round((35 * speedKmh / 30) * factor));

  const tip = metersToLatLng(innesco, lunghezza, propagationDeg);

  const baseWidth = lunghezza * Math.tan((semiAngleDeg * Math.PI) / 180);
  const baseLeft  = metersToLatLng(innesco, baseWidth, normalizeDegrees(propagationDeg - 90));
  const baseRight = metersToLatLng(innesco, baseWidth, normalizeDegrees(propagationDeg + 90));

  const midDist   = lunghezza * 0.5;
  const midSpread = midDist * Math.tan((semiAngleDeg * Math.PI) / 180);
  const midLeft  = metersToLatLng(
    metersToLatLng(innesco, midDist, propagationDeg),
    midSpread, normalizeDegrees(propagationDeg - 90)
  );
  const midRight = metersToLatLng(
    metersToLatLng(innesco, midDist, propagationDeg),
    midSpread, normalizeDegrees(propagationDeg + 90)
  );

  return { tip, baseLeft, baseRight, midLeft, midRight, lunghezza, ros };
}

// ─── Configurazione isocroni ──────────────────────────────────
const ISOCRONI_CFG = [
  { min: 15, color: '#fbbf24', weight: 1.5, dash: '8,5' },
  { min: 30, color: '#f97316', weight: 1.5, dash: '6,4' },
  { min: 60, color: '#ef4444', weight: 2,   dash: '5,3' },
] as const;

// ─────────────────────────────────────────────────────────────

const WindAnalysisLayer: React.FC<WindAnalysisLayerProps> = ({
  directionDeg,
  speedKmh,
  puntoInnesco,
  tipoVegetazione,
}) => {
  if (!puntoInnesco) return null;

  const propagationDeg = normalizeDegrees(directionDeg + 180);
  const { tip, baseLeft, baseRight, midLeft, midRight, lunghezza, ros } =
    buildTriangle(puntoInnesco, propagationDeg, speedKmh, tipoVegetazione);

  const propagationLabel = getWindLabel(propagationDeg);

  // Isocroni: raggi in metri, capped a 10 km per leggibilità
  const isocroni = ISOCRONI_CFG.map((cfg) => {
    const dist = Math.min(10000, ros * cfg.min);
    const areaHa = Math.round((Math.PI * dist * dist) / 10000);
    return { ...cfg, dist, areaHa };
  });

  const spottingDist = tipoVegetazione ? (SPOTTING_FUELS[tipoVegetazione] ?? 0) : 0;

  const conePolygon: [number, number][] = [
    puntoInnesco,
    midLeft,
    baseLeft,
    tip,
    baseRight,
    midRight,
  ];

  // Etichetta distanza leggibile per il tooltip del fronte
  const distLabel = lunghezza >= 1000
    ? `${(lunghezza / 1000).toFixed(1)} km`
    : `${lunghezza} m`;
  const vegeLabel = tipoVegetazione ? tipoVegetazione.replace(/_/g, ' ') : null;

  return (
    <>
      {/* ── Fill cono semitrasparente ── */}
      <Polygon
        positions={conePolygon}
        pathOptions={{
          color:       AIB_COLORS.conoPropagazione,
          weight:      1.5,
          fillColor:   AIB_COLORS.conoPropagazione,
          fillOpacity: 0.12,
          dashArray:   '6,5',
        }}
      />

      {/* ── Asse direttrice azzurro tratteggiato (innesco → tip) ── */}
      <Polyline
        positions={[puntoInnesco, tip]}
        pathOptions={{
          color:     AIB_COLORS.assePropagazion,
          weight:    2.5,
          opacity:   0.85,
          dashArray: '12,8',
        }}
      />

      {/* ── Lato sinistro — giallo (fianco lento) ── */}
      <Polyline
        positions={[puntoInnesco, midLeft, baseLeft, tip]}
        pathOptions={{
          color:   AIB_COLORS.fiancoSinistro,
          weight:  3,
          opacity: 0.9,
        }}
      />

      {/* ── Lato destro — arancione (fianco veloce) ── */}
      <Polyline
        positions={[puntoInnesco, midRight, baseRight, tip]}
        pathOptions={{
          color:   AIB_COLORS.fiancoDestro,
          weight:  3,
          opacity: 0.9,
        }}
      />

      {/* ── Vettore principale sull'asse (arancione pieno, più spesso) ── */}
      <Polyline
        positions={[puntoInnesco, tip]}
        pathOptions={{
          color:   AIB_COLORS.fiancoDestro,
          weight:  4,
          opacity: 0.95,
        }}
      />

      {/* ── Isocroni T+15 / T+30 / T+60 min ── */}
      {isocroni.map(({ min, dist, areaHa, color, weight, dash }) => (
        <Circle
          key={`iso-${min}`}
          center={puntoInnesco}
          radius={dist}
          pathOptions={{
            color,
            weight,
            opacity:     0.65,
            fillOpacity: 0,
            dashArray:   dash,
          }}
        >
          <Tooltip>
            <span style={{ fontSize: 12 }}>
              T+{min} min &nbsp;·&nbsp; ~{areaHa.toLocaleString('it-IT')} ha
              {dist >= 10000 ? ' (cap 10 km)' : ''}
            </span>
          </Tooltip>
        </Circle>
      ))}

      {/* ── Zona spotting (solo per combustibili a rischio braci) ── */}
      {spottingDist > 0 && (
        <Circle
          center={puntoInnesco}
          radius={spottingDist}
          pathOptions={{
            color:       '#a855f7',
            weight:      2,
            opacity:     0.75,
            fillColor:   '#a855f7',
            fillOpacity: 0.04,
            dashArray:   '4,7',
          }}
        >
          <Tooltip>
            <span style={{ fontSize: 12 }}>
              ⚡ Spotting · {vegeLabel} · fino a {(spottingDist / 1000).toFixed(1)} km
            </span>
          </Tooltip>
        </Circle>
      )}

      {/* ── Marker al fronte con tooltip permanente ── */}
      <CircleMarker
        center={tip}
        radius={5}
        pathOptions={{
          color:       AIB_COLORS.fiancoDestro,
          fillColor:   AIB_COLORS.fiancoDestro,
          fillOpacity: 0.85,
          weight:      2,
        }}
      >
        <Tooltip permanent direction="top" offset={[0, -8]}>
          <div style={{ fontSize: 12, lineHeight: 1.5, fontWeight: 600 }}>
            ↑ {propagationLabel} · {Math.round(propagationDeg)}°
            <br />
            <span style={{ fontWeight: 400 }}>
              ~{distLabel}{vegeLabel ? ` · ${vegeLabel}` : ''}
            </span>
          </div>
        </Tooltip>
      </CircleMarker>
    </>
  );
};

export default WindAnalysisLayer;
